/**
 * PhysicianAttestationBar — Moat 2.
 *
 * Renders the encounter attestation state machine UI:
 *   DRAFT    → yellow "DRAFT — Not Medical Record" banner + Attest dialog
 *   ATTESTED → green "ATTESTED BY Dr X (MD) on YYYY-MM-DD · SHA256 hash" banner
 *   AMENDED  → blue "AMENDED on YYYY-MM-DD by Dr Y" banner + history link
 *
 * Every signature is append-only and hash-chained in encounter_attestations.
 * Until ATTESTED, every PDF the patient sees carries the "DRAFT — Not Medical
 * Record" watermark. After ATTESTED, the PDF carries the physician's name,
 * credential, license, and the SHA-256 hash of the SOAP set at signing time.
 *
 * May 30, 2026. NO LLM in the attestation logic — pure deterministic.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, FileSignature, History, AlertTriangle, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function AttestDialog({ encounterId, currentStatus, onClose, onSigned }) {
  const isAmend = currentStatus === 'ATTESTED' || currentStatus === 'AMENDED';
  const [name, setName] = useState('');
  const [credential, setCredential] = useState('MD');
  const [license, setLicense] = useState('');
  const [statement, setStatement] = useState(
    'I have reviewed this encounter, the patient\'s history, the risk flags ' +
    'generated by the deterministic clinical engines, and the draft SOAP note. ' +
    'I attest to the clinical accuracy of this record.'
  );
  const [amendReason, setAmendReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) { setErr('Physician name is required.'); return; }
    if (isAmend && !amendReason.trim()) { setErr('Amendment reason is required when re-signing.'); return; }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API}/api/encounter/${encounterId}/attest`, {
        physician_name: name,
        physician_credential: credential,
        physician_license_number: license,
        attestation_statement: statement,
        amendment_reason: amendReason,
      });
      onSigned && onSigned(resp.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Attestation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="attest-dialog">
      <div className="bg-white rounded-lg max-w-xl w-full p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-slate-900">
            {isAmend ? 'Amend attestation' : 'Attest this encounter'}
          </h3>
          <button onClick={onClose} data-testid="attest-close" className="text-slate-500 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[11px] italic text-slate-600 mb-3">
          Your signature is append-only and hash-chained. It freezes the SOAP + risk-flag set as the
          medical record at this moment. Any subsequent change becomes a separate AMENDMENT, preserving
          the original.
        </p>
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] font-bold text-slate-700 block mb-0.5">Physician name *</label>
              <input value={name} onChange={e => setName(e.target.value)}
                data-testid="attest-name" placeholder="Dr. Full Name"
                className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-0.5">Credential</label>
              <select value={credential} onChange={e => setCredential(e.target.value)}
                data-testid="attest-credential"
                className="w-full text-xs border border-slate-300 rounded px-2 py-1">
                <option>MD</option><option>DO</option><option>MBBS</option><option>NP</option><option>PA</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-0.5">License number (optional)</label>
            <input value={license} onChange={e => setLicense(e.target.value)}
              data-testid="attest-license" placeholder="e.g. NY 123456"
              className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-0.5">Attestation statement</label>
            <textarea value={statement} onChange={e => setStatement(e.target.value)}
              data-testid="attest-statement" rows={3}
              className="w-full text-xs border border-slate-300 rounded px-2 py-1 leading-relaxed" />
          </div>
          {isAmend && (
            <div>
              <label className="text-[11px] font-bold text-red-700 block mb-0.5">Amendment reason *</label>
              <textarea value={amendReason} onChange={e => setAmendReason(e.target.value)}
                data-testid="attest-amend-reason" rows={2}
                placeholder="e.g., New lab result changed the assessment of..."
                className="w-full text-xs border border-red-300 rounded px-2 py-1" />
            </div>
          )}
        </div>
        {err && <div className="text-xs text-red-700 mt-2 italic" data-testid="attest-error">{err}</div>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose}
            data-testid="attest-cancel"
            className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={submitting}
            data-testid="attest-submit"
            className="text-xs font-bold px-4 py-1.5 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">
            {submitting ? 'Signing…' : isAmend ? 'Sign amendment' : 'Sign attestation'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PhysicianAttestationBar({ encounterId, initialAttestation }) {
  const [attn, setAttn] = useState(initialAttestation || { status: 'DRAFT' });
  const [history, setHistory] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!encounterId) return;
    axios.get(`${API}/api/encounter/${encounterId}/attestation`).then(r => {
      setAttn(r.data.current || { status: r.data.status || 'DRAFT' });
      setHistory(r.data.history || []);
    }).catch(() => {});
  }, [encounterId]);

  const refresh = async () => {
    const r = await axios.get(`${API}/api/encounter/${encounterId}/attestation`);
    setAttn(r.data.current || { status: r.data.status || 'DRAFT' });
    setHistory(r.data.history || []);
    setShowDialog(false);
  };

  const status = attn.status || 'DRAFT';
  const palette = {
    DRAFT:    { bg: 'bg-amber-50',    border: 'border-amber-500',    text: 'text-amber-900',    icon: AlertTriangle },
    ATTESTED: { bg: 'bg-emerald-50',  border: 'border-emerald-600',  text: 'text-emerald-900',  icon: ShieldCheck },
    AMENDED:  { bg: 'bg-sky-50',      border: 'border-sky-600',      text: 'text-sky-900',      icon: FileSignature },
  }[status] || { bg: 'bg-slate-50', border: 'border-slate-300', text: 'text-slate-800', icon: AlertTriangle };
  const Icon = palette.icon;

  return (
    <section className={`rounded-lg border-2 ${palette.border} ${palette.bg} p-3 mb-4`} data-testid="attestation-bar">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 mt-0.5 ${palette.text}`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[10px] font-bold uppercase tracking-widest ${palette.text}`} data-testid="attestation-status">
              {status === 'DRAFT' ? 'DRAFT — Not Medical Record' :
               status === 'ATTESTED' ? 'ATTESTED — Medical Record' :
               status === 'AMENDED' ? 'AMENDED — Medical Record (revised)' :
               status}
            </span>
          </div>
          {status === 'DRAFT' ? (
            <p className="text-xs text-amber-900 leading-relaxed">
              This encounter has not been signed by a physician. Until attested it is a draft only and
              cannot be relied on for treatment, billing, or legal record. Patient-facing PDFs are
              watermarked &ldquo;DRAFT — Not Medical Record&rdquo;.
            </p>
          ) : (
            <div className="text-xs text-slate-700 leading-relaxed">
              <div data-testid="attestation-byline">
                Signed by <strong>{attn.physician_name}</strong>
                {attn.physician_credential ? ` (${attn.physician_credential})` : ''}
                {attn.physician_license_number ? ` · License ${attn.physician_license_number}` : ''}
                {' '}on <strong>{attn.signedAt ? attn.signedAt.replace('T', ' ').slice(0, 19) + ' UTC' : '—'}</strong>
              </div>
              <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                signature SHA-256 {attn.signature_hash ? attn.signature_hash.slice(0, 24) + '…' : '—'}
                {attn.encounter_hash_at_sign && ` · encounter SHA-256 ${attn.encounter_hash_at_sign.slice(0, 16)}…`}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {status === 'DRAFT' ? (
            <button onClick={() => setShowDialog(true)}
              data-testid="attestation-attest-btn"
              className="text-xs font-bold px-3 py-1.5 rounded bg-emerald-700 text-white hover:bg-emerald-800">
              Attest this encounter
            </button>
          ) : (
            <>
              <button onClick={() => setShowDialog(true)}
                data-testid="attestation-amend-btn"
                className="text-xs font-bold px-3 py-1.5 rounded bg-sky-700 text-white hover:bg-sky-800">
                Amend
              </button>
              {history.length > 1 && (
                <button onClick={() => setShowHistory(s => !s)}
                  data-testid="attestation-history-toggle"
                  className="text-[10px] text-slate-600 underline flex items-center gap-1">
                  <History className="w-3 h-3" /> {history.length} signatures
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showHistory && history.length > 0 && (
        <div className="mt-3 border-t border-slate-200 pt-2" data-testid="attestation-history">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Signature history (newest first)</div>
          <ul className="space-y-1">
            {history.map((h, i) => (
              <li key={h.attestationId || i} className="text-[11px] text-slate-700 bg-white rounded px-2 py-1 border border-slate-200">
                <strong>{h.status_set_to}</strong> by <strong>{h.physician_name}</strong> ({h.physician_credential})
                on {h.signedAt?.replace('T', ' ').slice(0, 19)} UTC ·
                <span className="font-mono text-slate-500"> sig {h.signature_hash?.slice(0, 12)}…</span>
                {h.amendment_reason && <div className="italic text-slate-600 mt-0.5">amendment: {h.amendment_reason}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {showDialog && (
        <AttestDialog
          encounterId={encounterId}
          currentStatus={status}
          onClose={() => setShowDialog(false)}
          onSigned={() => refresh()}
        />
      )}
    </section>
  );
}
